// Image carousel (swiper): one slide per view, swipe / drag-scroll on touch, previous / next buttons, dot indicators, arrow keys,
// optional autoplay (paused on hover / focus and with reduced motion). Slides share one aspect ratio so the page never jumps.
// Props: items [{u, c}], ratio ('16:9' | '21:9' | '3:2' | '4:3' | '1:1'), orient ('landscape' | 'portrait': portrait turns the ratio
// around, e.g. 3:2 becomes 2:3, and keeps the carousel at most 75vh tall), fit ('cover' | 'contain'), auto (bool)
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useRef, useEffect, useCallback } = React;

  const RATIOS = { '16:9': '16 / 9', '21:9': '21 / 9', '3:2': '3 / 2', '4:3': '4 / 3', '1:1': '1 / 1' };
  // Portfolio uploads are served as the 1200px copy (?w=1200): the smaller 480/800px copies looked soft in a full-width slide.
  const UPLOAD_IMG = /^\/uploads\/portfolio\/[\w.-]+\.(?!gif$)\w+$/i;
  const slideSrc = u => (UPLOAD_IMG.test(u || '') ? `${u}?w=1200` : u);
  const nav = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'color-mix(in srgb, var(--m3-surface-container) 90%, transparent)' };

  function ImageCarousel({ items = [], ratio = '16:9', orient = 'landscape', fit = 'cover', auto = false }) {
    const { IconButton, GoogleIcon } = window.Components;
    const slides = items.filter(it => it && it.u);
    const track = useRef(null);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const n = slides.length;

    const goTo = useCallback(i => {
      const el = track.current;
      if (!el || !n) return;
      const k = (i + n) % n;
      el.scrollTo({ left: k * el.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, [n]);

    const onScroll = () => {
      const el = track.current;
      if (el && el.clientWidth) setIndex(Math.min(n - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth))));
    };

    useEffect(() => {
      if (!auto || paused || n < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
      const id = setInterval(() => goTo(index + 1), 5000);
      return () => clearInterval(id);
    }, [auto, paused, n, index, goTo]);

    if (!n) return null;
    const current = slides[Math.min(index, n - 1)];
    const [w, h] = (RATIOS[ratio] || RATIOS['16:9']).split(' / ').map(Number);
    const portrait = orient === 'portrait' && w !== h;
    const slideRatio = portrait ? `${h} / ${w}` : `${w} / ${h}`;
    return (
      <Box component="section" role="group" aria-roledescription="carousel" aria-label="Image carousel" sx={{ my: 3, ...(portrait && { width: `min(100%, calc(75vh * ${h} / ${w}))`, mx: 'auto' }) }}
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
        <Box sx={{ position: 'relative' }}>
          <Box
            ref={track} tabIndex={0} onScroll={onScroll}
            onKeyDown={e => { if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); } if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); } }}
            sx={{
              display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
              borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-surface-container-highest)', outline: 'none',
              '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2 }
            }}
          >
            {slides.map((it, i) => (
              <Box key={i} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${n}`} sx={{ flex: '0 0 100%', scrollSnapAlign: 'start', aspectRatio: slideRatio }}>
                <Box component="img" src={slideSrc(it.u)} alt={it.c || ''} loading={i === 0 ? 'eager' : 'lazy'} draggable={false}
                  sx={{ display: 'block', width: '100%', height: '100%', objectFit: fit === 'contain' ? 'contain' : 'cover' }} />
              </Box>
            ))}
          </Box>
          {n > 1 && (
            <>
              <IconButton aria-label="Previous image" onClick={() => goTo(index - 1)} sx={{ ...nav, left: 8 }}><GoogleIcon name="chevron_left" size={24} /></IconButton>
              <IconButton aria-label="Next image" onClick={() => goTo(index + 1)} sx={{ ...nav, right: 8 }}><GoogleIcon name="chevron_right" size={24} /></IconButton>
            </>
          )}
        </Box>
        {n > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0, mt: 0.5 }}>
            {slides.map((_, i) => (
              // 24px touch target around the visible 8px dot (the dot is the ::before)
              <Box key={i} component="button" type="button" aria-label={`Go to image ${i + 1}`} aria-current={i === index ? 'true' : undefined} onClick={() => goTo(i)}
                sx={{ p: 0, border: 0, background: 'none', cursor: 'pointer', height: 24, minWidth: 24, width: i === index ? 40 : 24, display: 'grid', placeItems: 'center', transition: 'width 200ms',
                  '@media (pointer: coarse)': { height: 44, minWidth: 44, width: i === index ? 56 : 44 },
                  '&::before': { content: '""', display: 'block', height: 8, width: i === index ? 24 : 8, borderRadius: 'var(--m3-shape-full)', transition: 'width 200ms, background-color 200ms', backgroundColor: i === index ? 'var(--m3-primary)' : 'var(--m3-outline-variant)' },
                  '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: -3, borderRadius: 'var(--m3-shape-full)' } }} />
            ))}
          </Box>
        )}
        {current.c && <Typography component="p" variant="body2" aria-live="polite" sx={{ mt: 1, textAlign: 'center', color: 'var(--m3-secondary)' }}>{current.c}</Typography>}
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.ImageCarousel = ImageCarousel;
  window.Components.CAROUSEL_RATIOS = Object.keys(RATIOS);
})();
